import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import Roulette from '../components/Roulette.jsx'
import WeaponArt from '../components/WeaponArt.jsx'
import CoinIcon from '../components/CoinIcon.jsx'
import { formatCoinsNum, RARITY_COLORS, RARITY_LABELS, WEAR_FULL } from '../lib/format.js'
import '../styles/case-open.css'

const COUNTS = [1, 2, 3, 4, 5]

function getWearLabel(item) {
  if (!item) return null
  if (item.wearLabel) return item.wearLabel
  if (item.wear && WEAR_FULL[item.wear]) return WEAR_FULL[item.wear]
  return null
}

export default function CaseOpen() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, setBalance } = useAuth()
  const toast = useToast()

  const [caseData, setCaseData] = useState(null)
  const [error, setError] = useState(null)
  const [count, setCount] = useState(1)
  const [fastMode, setFastMode] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)

  const [spinning, setSpinning] = useState(false)
  const [opening, setOpening] = useState(false)
  const [result, setResult] = useState(null)
  const [showDrops, setShowDrops] = useState(false)
  const [soldIds, setSoldIds] = useState([])
  const [sellingAll, setSellingAll] = useState(false)
  const [sellingItemIds, setSellingItemIds] = useState([])

  useEffect(() => {
    setCaseData(null)
    api
      .caseById(id)
      .then(({ case: data }) => setCaseData(data))
      .catch((err) => setError(err.message))
  }, [id])

  const totalCost = caseData ? caseData.priceKopecks * count : 0
  const canAfford = user ? user.balance >= totalCost : false
  const pool = useMemo(() => caseData?.contents ?? [], [caseData])

  // Запуск открытия кейса
  async function handleOpen() {
    if (!user) {
      navigate('/login', { state: { from: `/cases/${id}` } })
      return
    }
    if (spinning || opening) return

    setOpening(true)
    setShowDrops(false)
    setSoldIds([])
    setSellingItemIds([])
    setError(null)
    setResult(null)

    try {
      const response = await api.openCase(id, count)
      setBalance(response.balance)
      setResult(response)
      setSpinning(true)
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Не удалось открыть кейс'
      setError(message)
      toast.error(message)
    } finally {
      setOpening(false)
    }
  }

  // Завершение анимации прокрутки барабана
  function handleSpinFinish() {
    setSpinning(false)
    setShowDrops(true)
  }

  // Забрать предмет(ы) в инвентарь и вернуться к кейсу
  function handleKeep() {
    setShowDrops(false)
    const keptCount = result?.drops?.filter((d) => !soldIds.includes(d.inventoryId)).length ?? 0
    if (keptCount > 0) {
      toast.success(
        keptCount === 1
          ? 'Предмет сохранён в инвентаре!'
          : `${keptCount} предм. сохранено в инвентаре!`
      )
    }
    setSoldIds([])
    setSellingItemIds([])
    setResult(null)
  }

  // Продажа одного конкретного предмета из выигрыша
  async function handleSellSingle(drop) {
    if (!drop || soldIds.includes(drop.inventoryId) || sellingItemIds.includes(drop.inventoryId) || sellingAll) return

    setSellingItemIds((prev) => [...prev, drop.inventoryId])

    try {
      const response = await api.sell([drop.inventoryId])
      setBalance(response.balance)
      setSoldIds((prev) => [...prev, drop.inventoryId])
      toast.success(`Продано за +${formatCoinsNum(response.payoutKopecks || drop.priceKopecks)}`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSellingItemIds((prev) => prev.filter((id) => id !== drop.inventoryId))
    }
  }

  // Продажа всех оставшихся выпавших предметов
  async function handleSellAll() {
    if (!result) return
    const unsoldDrops = result.drops.filter((d) => !soldIds.includes(d.inventoryId))
    if (unsoldDrops.length === 0) {
      return
    }

    setSellingAll(true)
    const idsToSell = unsoldDrops.map((d) => d.inventoryId)

    try {
      const response = await api.sell(idsToSell)
      setBalance(response.balance)
      setSoldIds((prev) => [...prev, ...idsToSell])
      toast.success(`Продано за +${formatCoinsNum(response.payoutKopecks)}`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSellingAll(false)
    }
  }

  if (error && !caseData) {
    return (
      <div className="cm-case-page container center">
        <div className="alert alert-error" style={{ marginBottom: 20 }}>{error}</div>
        <Link to="/" className="cm-back-btn">
          <span className="cm-back-icon-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </span>
          <span className="cm-back-label">Назад</span>
        </Link>
      </div>
    )
  }

  if (!caseData) {
    return (
      <div className="cm-case-page container">
        <div className="skeleton" style={{ height: 420, borderRadius: 24 }} />
      </div>
    )
  }

  const unsoldDrops = result ? result.drops.filter((d) => !soldIds.includes(d.inventoryId)) : []
  const unsoldTotal = unsoldDrops.reduce((sum, d) => sum + d.priceKopecks, 0)
  const singleDrop = result?.drops?.length === 1 ? result.drops[0] : null
  const isOpening = spinning || opening
  const hasResult = !isOpening && Boolean(result)
  const isSingleSold = singleDrop ? soldIds.includes(singleDrop.inventoryId) : false
  const isSingleSelling = singleDrop ? sellingItemIds.includes(singleDrop.inventoryId) : false

  return (
    <div className="cm-case-page container">
      {/* Заголовок кейса с кнопкой "Назад" на одном уровне */}
      <header className="cm-case-header">
        <Link to="/" className="cm-back-btn" title="Вернуться к кейсам">
          <span className="cm-back-icon-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </span>
          <span className="cm-back-label">Назад</span>
        </Link>

        <h1 className="cm-case-title">
          {caseData.name}
        </h1>
      </header>

      {/* Арена открытия */}
      <section className="cm-stage-arena">
        <div className="cm-stage-inner">
          <Roulette
            pool={pool}
            winners={result?.drops}
            count={count}
            spinning={spinning}
            fastMode={fastMode}
            soundEnabled={soundEnabled}
            onFinish={handleSpinFinish}
            onSellSingle={handleSellSingle}
            soldIds={soldIds}
            sellingItemIds={sellingItemIds}
          />
        </div>

        {/* Панель управления */}
        <div className="cm-controls-bar">
          {isOpening ? (
            /* Во время открытия: просто текст ОТКРЫВАЕМ */
            <div className="cm-opening-status" aria-live="polite">
              ОТКРЫВАЕМ...
            </div>
          ) : hasResult ? (
            /* В конце открытия */
            count === 1 ? (
              /* Для х1: ПРОДАТЬ и ЗАБРАТЬ */
              <>
                {singleDrop && (
                  <button
                    type="button"
                    className={`cm-action-btn-sell ${isSingleSold ? 'is-sold' : ''}`}
                    onClick={() => handleSellSingle(singleDrop)}
                    disabled={isSingleSold || isSingleSelling}
                  >
                    <span className="cm-action-btn-label">
                      {isSingleSold
                        ? '✓ Продано'
                        : isSingleSelling
                          ? 'Продаём…'
                          : 'Продать'}
                    </span>
                    {!isSingleSold && (
                      <span className="cm-action-btn-price">
                        <span>{formatCoinsNum(singleDrop.priceKopecks)}</span>
                        <CoinIcon size="sm" />
                      </span>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="cm-action-btn-keep"
                  onClick={handleKeep}
                >
                  <span className="cm-action-btn-label">Забрать</span>
                </button>
              </>
            ) : (
              /* Для х2-х5: только кнопка ЗАБРАТЬ */
              <button
                type="button"
                className="cm-action-btn-keep cm-action-btn-keep-main"
                onClick={handleKeep}
              >
                <span className="cm-action-btn-label">Забрать</span>
              </button>
            )
          ) : (
            /* Исходное состояние до открытия */
            <>
              {/* Селектор количества [1] [2] [3] [4] [5] */}
              <div className="cm-count-selector" role="group" aria-label="Количество кейсов">
                {COUNTS.map((val) => (
                  <button
                    key={val}
                    type="button"
                    className={`cm-count-btn ${count === val ? 'is-active' : ''}`}
                    onClick={() => {
                      setCount(val)
                      setResult(null)
                      setShowDrops(false)
                    }}
                  >
                    <span className="cm-count-label">
                      <span className="cm-count-x">х</span>
                      <span className="cm-count-num">{val}</span>
                    </span>
                  </button>
                ))}
              </div>

              {/* Главная кнопка "Открыть кейс" */}
              {!user ? (
                <a href="/api/auth/steam" className="cm-spin-btn">
                  <span className="cm-spin-btn-label">Войти через Steam</span>
                </a>
              ) : (
                <button
                  type="button"
                  className="cm-spin-btn"
                  onClick={handleOpen}
                  disabled={!canAfford}
                >
                  <span className="cm-spin-btn-label">
                    {!canAfford ? 'Недостаточно средств' : 'Открыть кейс'}
                  </span>
                  <span className="cm-spin-btn-price">
                    <span>{formatCoinsNum(totalCost)}</span>
                    <CoinIcon size="sm" />
                  </span>
                </button>
              )}

              {/* Турбо-режим */}
              <button
                type="button"
                className={`cm-turbo-toggle ${fastMode ? 'is-active' : ''}`}
                onClick={() => setFastMode((prev) => !prev)}
                title={fastMode ? 'Выключить турбо-режим' : 'Включить турбо-режим'}
                aria-label="Турбо-режим"
              >
                <svg className="cm-turbo-icon" viewBox="0 0 24 24">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </button>

              {/* Звук */}
              <button
                type="button"
                className={`cm-sound-toggle ${soundEnabled ? 'is-active' : ''}`}
                onClick={() => setSoundEnabled((prev) => !prev)}
                title={soundEnabled ? 'Выключить звук' : 'Включить звук'}
              >
                {soundEnabled ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      </section>

      {/* ═════════════════════════════════════════════════════════════
         РАЗДЕЛ "СОДЕРЖИМОЕ КЕЙСА"
         ═════════════════════════════════════════════════════════════ */}
      <section className="cm-contents-section">
        <div className="cm-contents-head">
          <div className="cm-contents-line" />
          <h2 className="cm-contents-title">Содержимое кейса</h2>
          <div className="cm-contents-line" />
        </div>

        <div className="cm-contents-grid">
          {caseData.contents.map((item) => (
            <div
              key={item.id}
              className="cm-content-card"
              style={{ '--card-rarity': RARITY_COLORS[item.rarity] || '#8b5cf6' }}
            >
              {/* Арт предмета */}
              <WeaponArt image={item.image} rarity={item.rarity} title={item.skin} size="lg" />

              {/* Мета-информация */}
              <div className="cm-content-meta">
                <span className="cm-content-name">{item.name}</span>
                <span className="cm-content-skin">{item.skin}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
